import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Search, Edit, GraduationCap, Users, Clock, Building, Loader2, ExternalLink, ChevronDown, ChevronUp, User, Save, Trash2, CalendarClock, RefreshCw, Hash, Check, X } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { canEdit, isAdmin } from '@/lib/auth';
import api from '@/lib/api';

export default function ClassGroups() {
  const [classGroups, setClassGroups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [filterShift, setFilterShift] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [availableYears, setAvailableYears] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [seeding, setSeeding] = useState(false);
  // Estado para aba de alunos
  const [expandedClass, setExpandedClass] = useState(null);
  const [classStudents, setClassStudents] = useState({});
  const [loadingStudents, setLoadingStudents] = useState(false);
  // Estado para horários da turma
  const [classTimeSlots, setClassTimeSlots] = useState({});
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);
  const [savingTimeSlots, setSavingTimeSlots] = useState(false);
  const [expandedTab, setExpandedTab] = useState('alunos');
  
  // Estado para números de chamada
  const [generatingCallNumbers, setGeneratingCallNumbers] = useState({});
  const [editingCallNumber, setEditingCallNumber] = useState(null);
  const [newCallNumber, setNewCallNumber] = useState('');
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    acronym: '',
    year: 2026,
    course_series: '',
    course_id: '',
    education_level: '',
    shift: '',
    status: 'Aberta',
    max_students: '',
    min_students: '',
    teacher: '',
    room: '',
    notes: '',
    google_classroom_link: ''
  });

  const shifts = ['Manhã', 'Tarde', 'Noite'];
  const levels = ['Educação Infantil', 'Ensino Fundamental I', 'Ensino Fundamental II', 'Ensino Médio'];
  const statuses = ['Aberta', 'Fechada', 'Em Andamento'];

  useEffect(() => {
    loadClassGroups();
    loadCourses();
  }, [filterYear]);

  // V6.5.3 · Carrega dinamicamente os anos existentes em class_groups + ano atual e próximo.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/class-groups');
        const cy = new Date().getFullYear();
        const set = new Set([cy, cy + 1]);
        (data || []).forEach((g) => { if (g && g.year) set.add(Number(g.year)); });
        setAvailableYears([...set].filter(Boolean).sort((a, b) => b - a));
      } catch (_) {
        const cy = new Date().getFullYear();
        setAvailableYears([cy + 1, cy, cy - 1, cy - 2]);
      }
    })();
  }, []);

  const loadCourses = async () => {
    try {
      const response = await api.get('/courses');
      setCourses(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar cursos:', error);
    }
  };

  const handleCourseChange = (courseId) => {
    const course = courses.find(c => c.id === courseId);
    if (course) {
      setFormData({
        ...formData,
        course_id: courseId,
        course_series: course.name,
        education_level: course.course_type || formData.education_level
      });
    }
  };

  const loadClassGroups = async () => {
    setLoading(true);
    try {
      const params = filterYear && filterYear !== 'all' ? `?year=${filterYear}` : '';
      const response = await api.get(`/class-groups${params}`);
      setClassGroups(response.data);
    } catch (error) {
      toast.error('Erro ao carregar turmas');
    } finally {
      setLoading(false);
    }
  };

  // Carregar alunos de uma turma
  const loadStudentsForClass = async (classGroup) => {
    if (expandedClass === classGroup.id) {
      setExpandedClass(null);
      return;
    }
    
    setExpandedClass(classGroup.id);
    setExpandedTab('alunos');
    
    if (classStudents[classGroup.id]) {
      return; // Já carregou
    }
    
    setLoadingStudents(true);
    try {
      const response = await api.get(`/students?class_id=${classGroup.id}`);
      const sortedStudents = (response.data || []).sort((a, b) => 
        (a.call_number || 999) - (b.call_number || 999)
      );
      setClassStudents(prev => ({
        ...prev,
        [classGroup.id]: sortedStudents
      }));
    } catch (error) {
      toast.error('Erro ao carregar alunos da turma');
    } finally {
      setLoadingStudents(false);
    }
  };

  // Carregar horários da turma
  const loadTimeSlotsForClass = async (classGroupId) => {
    if (classTimeSlots[classGroupId]) {
      return classTimeSlots[classGroupId];
    }
    
    setLoadingTimeSlots(true);
    try {
      const response = await api.get(`/time-slots/class/${classGroupId}`);
      const data = response.data;
      
      // Se não tem slots configurados, usar o padrão
      const slots = data.slots || getDefaultTimeSlots();
      const source = data.source || 'default';
      
      setClassTimeSlots(prev => ({
        ...prev,
        [classGroupId]: { slots, source }
      }));
      
      return { slots, source };
    } catch (error) {
      console.error('Erro ao carregar horários:', error);
      const defaultSlots = getDefaultTimeSlots();
      setClassTimeSlots(prev => ({
        ...prev,
        [classGroupId]: { slots: defaultSlots, source: 'default' }
      }));
      return { slots: defaultSlots, source: 'default' };
    } finally {
      setLoadingTimeSlots(false);
    }
  };

  // Horários padrão
  const getDefaultTimeSlots = () => [
    { id: '07:05', start: '07:05', end: '07:50', label: '07:05 - 07:50', turno: 'manha' },
    { id: '07:50', start: '07:50', end: '08:35', label: '07:50 - 08:35', turno: 'manha' },
    { id: '08:35', start: '08:35', end: '09:20', label: '08:35 - 09:20', turno: 'manha' },
    { id: '09:20', start: '09:20', end: '09:40', label: '09:20 - 09:40 (Intervalo)', turno: 'manha' },
    { id: '09:40', start: '09:40', end: '10:25', label: '09:40 - 10:25', turno: 'manha' },
    { id: '10:25', start: '10:25', end: '11:10', label: '10:25 - 11:10', turno: 'manha' },
    { id: '11:10', start: '11:10', end: '11:55', label: '11:10 - 11:55', turno: 'manha' },
    { id: '13:00', start: '13:00', end: '13:50', label: '13:00 - 13:50', turno: 'tarde' },
    { id: '13:50', start: '13:50', end: '14:35', label: '13:50 - 14:35', turno: 'tarde' },
    { id: '14:35', start: '14:35', end: '15:20', label: '14:35 - 15:20', turno: 'tarde' },
    { id: '15:20', start: '15:20', end: '15:40', label: '15:20 - 15:40 (Intervalo)', turno: 'tarde' },
    { id: '15:40', start: '15:40', end: '16:25', label: '15:40 - 16:25', turno: 'tarde' },
    { id: '16:25', start: '16:25', end: '17:10', label: '16:25 - 17:10', turno: 'tarde' },
  ];

  // Atualizar um slot da turma
  const handleTimeSlotChange = (classGroupId, index, field, value) => {
    setClassTimeSlots(prev => {
      const current = prev[classGroupId] || { slots: getDefaultTimeSlots(), source: 'default' };
      const newSlots = [...current.slots];
      newSlots[index] = { ...newSlots[index], [field]: value };
      
      // Atualizar label automaticamente
      if (field === 'start' || field === 'end') {
        newSlots[index].label = `${newSlots[index].start} - ${newSlots[index].end}`;
        newSlots[index].id = newSlots[index].start;
      }
      
      return {
        ...prev,
        [classGroupId]: { slots: newSlots, source: 'custom' }
      };
    });
  };

  // Adicionar novo slot
  const handleAddTimeSlot = (classGroupId, turno) => {
    setClassTimeSlots(prev => {
      const current = prev[classGroupId] || { slots: getDefaultTimeSlots(), source: 'default' };
      const newSlot = {
        id: `${Date.now()}`,
        start: turno === 'manha' ? '07:00' : '13:00',
        end: turno === 'manha' ? '07:45' : '13:45',
        label: turno === 'manha' ? '07:00 - 07:45' : '13:00 - 13:45',
        turno
      };
      return {
        ...prev,
        [classGroupId]: { slots: [...current.slots, newSlot], source: 'custom' }
      };
    });
  };

  // Remover slot
  const handleRemoveTimeSlot = (classGroupId, index) => {
    setClassTimeSlots(prev => {
      const current = prev[classGroupId] || { slots: [], source: 'default' };
      const newSlots = current.slots.filter((_, i) => i !== index);
      return {
        ...prev,
        [classGroupId]: { slots: newSlots, source: 'custom' }
      };
    });
  };

  // Salvar horários da turma
  const handleSaveTimeSlots = async (classGroupId) => {
    const data = classTimeSlots[classGroupId];
    if (!data) return;
    
    setSavingTimeSlots(true);
    try {
      await api.post(`/time-slots/class/${classGroupId}`, data.slots);
      toast.success('Horários da turma salvos com sucesso!');
      setClassTimeSlots(prev => ({
        ...prev,
        [classGroupId]: { ...data, source: 'custom' }
      }));
    } catch (error) {
      toast.error('Erro ao salvar horários');
    } finally {
      setSavingTimeSlots(false);
    }
  };

  // Resetar para horários padrão
  const handleResetTimeSlots = async (classGroupId) => {
    try {
      await api.delete(`/time-slots/class/${classGroupId}`);
      setClassTimeSlots(prev => ({
        ...prev,
        [classGroupId]: { slots: getDefaultTimeSlots(), source: 'default' }
      }));
      toast.success('Turma voltou a usar horários padrão');
    } catch (error) {
      toast.error('Erro ao resetar horários');
    }
  };

  // Gerar números de chamada para turma (ordem alfabética)
  const handleGenerateCallNumbers = async (classGroupId, e) => {
    if (e) e.stopPropagation();
    
    setGeneratingCallNumbers(prev => ({ ...prev, [classGroupId]: true }));
    try {
      const res = await api.post(`/students/generate-call-numbers/${classGroupId}`);
      toast.success(res.data.message || 'Números de chamada gerados!');
      // Recarregar alunos da turma para atualizar os números
      await loadStudentsForClassSilent(classGroupId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar números');
    } finally {
      setGeneratingCallNumbers(prev => ({ ...prev, [classGroupId]: false }));
    }
  };

  // Carregar alunos silenciosamente (sem loading visual)
  const loadStudentsForClassSilent = async (classGroupId) => {
    try {
      const response = await api.get(`/students?class_id=${classGroupId}`);
      const sortedStudents = (response.data || []).sort((a, b) => 
        (a.call_number || 999) - (b.call_number || 999)
      );
      setClassStudents(prev => ({
        ...prev,
        [classGroupId]: sortedStudents
      }));
    } catch (error) {
      console.error('Erro ao carregar alunos:', error);
    }
  };

  // Editar número de chamada manualmente (apenas admin)
  const handleSaveCallNumber = async (studentId, classGroupId) => {
    if (!newCallNumber || isNaN(parseInt(newCallNumber))) {
      toast.error('Digite um número válido');
      return;
    }
    
    try {
      await api.put(`/students/${studentId}/call-number`, null, {
        params: { call_number: parseInt(newCallNumber) }
      });
      toast.success('Número de chamada atualizado!');
      setEditingCallNumber(null);
      setNewCallNumber('');
      // Recarregar alunos
      await loadStudentsForClassSilent(classGroupId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar');
    }
  };

  const cancelEditCallNumber = () => {
    setEditingCallNumber(null);
    setNewCallNumber('');
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      acronym: '',
      year: 2026,
      course_series: '',
      education_level: '',
      shift: '',
      status: 'Aberta',
      max_students: '',
      min_students: '',
      teacher: '',
      room: '',
      notes: '',
      google_classroom_link: ''
    });
    setEditing(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.code || !formData.name || !formData.course_series) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    
    try {
      // V6.5.4 · Sanitiza course_id vazio para não sobrescrever valor salvo com string vazia.
      const { course_id, ...rest } = formData;
      const data = {
        ...rest,
        ...(course_id ? { course_id } : {}),
        year: parseInt(formData.year),
        max_students: formData.max_students ? parseInt(formData.max_students) : null,
        min_students: formData.min_students ? parseInt(formData.min_students) : null
      };

      // V6.5.4 · Consistência ano: turma.year deve bater com curso.year
      if (course_id) {
        const linked = courses.find((c) => c.id === course_id);
        if (linked && linked.year && parseInt(formData.year) !== parseInt(linked.year)) {
          toast.error(`Turma ${data.year} não pode apontar para curso ${linked.year}. Escolha um curso de ${data.year}.`);
          return;
        }
      }

      if (editing) {
        await api.put(`/class-groups/${editing.id}`, data);
        toast.success('Turma atualizada!');
      } else {
        await api.post('/class-groups', data);
        toast.success('Turma criada!');
      }
      
      setDialogOpen(false);
      resetForm();
      loadClassGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar turma');
    }
  };

  const handleEdit = (group) => {
    setEditing(group);
    setFormData({
      code: group.code || '',
      name: group.name || '',
      acronym: group.acronym || '',
      year: group.year || 2026,
      course_id: group.course_id || '',   // V6.5.4 · faltava restaurar → select ficava vazio no reabrir
      course_series: group.course_series || '',
      education_level: group.education_level || '',
      shift: group.shift || '',
      status: group.status || 'Aberta',
      max_students: group.max_students || '',
      min_students: group.min_students || '',
      teacher: group.teacher || '',
      room: group.room || '',
      notes: group.notes || '',
      google_classroom_link: group.google_classroom_link || ''
    });
    setDialogOpen(true);
  };

  const handleSeedDefault = async () => {
    setSeeding(true);
    try {
      const response = await api.post('/class-groups/seed-default');
      toast.success(response.data.message);
      loadClassGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar turmas');
    } finally {
      setSeeding(false);
    }
  };

  const filteredGroups = classGroups.filter(g => {
    const matchesSearch = g.name.toLowerCase().includes(search.toLowerCase()) ||
                          g.code.toLowerCase().includes(search.toLowerCase()) ||
                          g.course_series?.toLowerCase().includes(search.toLowerCase());
    const matchesShift = filterShift === 'all' || g.shift === filterShift;
    const matchesLevel = filterLevel === 'all' || g.education_level === filterLevel;
    return matchesSearch && matchesShift && matchesLevel;
  });

  // Agrupar por nível de ensino (com separação de Fundamental I e II)
  const groupedByLevel = filteredGroups.reduce((acc, g) => {
    let level = g.education_level || 'Outros';
    
    // Se for E.F1, separar em Fundamental I ou II baseado no ano
    if (level === 'Ensino Fundamental' || g.name?.includes('E.F1')) {
      if (g.name?.includes('6º') || g.name?.includes('7º') || g.name?.includes('8º') || g.name?.includes('9º')) {
        level = 'Ensino Fundamental II';
      } else {
        level = 'Ensino Fundamental I';
      }
    }
    
    if (!acc[level]) acc[level] = [];
    acc[level].push(g);
    return acc;
  }, {});

  const getShiftBadge = (shift) => {
    const colors = {
      'Manhã': 'bg-yellow-100 text-yellow-700',
      'Tarde': 'bg-blue-100 text-blue-700',
      'Noite': 'bg-purple-100 text-purple-700'
    };
    return colors[shift] || 'bg-gray-100 text-gray-700';
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Aberta': 'bg-green-100 text-green-700',
      'Fechada': 'bg-red-100 text-red-700',
      'Em Andamento': 'bg-blue-100 text-blue-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Ordem de exibição dos níveis
  const levelOrder = ['Educação Infantil', 'Ensino Fundamental I', 'Ensino Fundamental II', 'Ensino Médio', 'Outros'];
  
  // Ordenar níveis
  const sortedLevels = Object.keys(groupedByLevel).sort((a, b) => {
    return levelOrder.indexOf(a) - levelOrder.indexOf(b);
  });

  const getLevelIcon = (level) => {
    const icons = {
      'Educação Infantil': '🧒',
      'Ensino Fundamental I': '📚',
      'Ensino Fundamental II': '📖',
      'Ensino Médio': '🎓'
    };
    return icons[level] || '📋';
  };

  const getLevelColor = (level) => {
    const colors = {
      'Educação Infantil': 'border-l-pink-500',
      'Ensino Fundamental I': 'border-l-green-500',
      'Ensino Fundamental II': 'border-l-blue-500',
      'Ensino Médio': 'border-l-purple-500'
    };
    return colors[level] || 'border-l-gray-500';
  };

  // Componente de linha expandida com abas (Alunos e Horários)
  const renderExpandedRow = (group) => {
    if (expandedClass !== group.id) return null;
    
    const timeSlotData = classTimeSlots[group.id] || { slots: getDefaultTimeSlots(), source: 'default' };
    
    return (
      <TableRow>
        <TableCell colSpan={5} className="bg-muted/30 p-4">
          <Tabs value={expandedTab} onValueChange={(v) => {
            setExpandedTab(v);
            if (v === 'horarios') loadTimeSlotsForClass(group.id);
          }}>
            <TabsList className="mb-4">
              <TabsTrigger value="alunos" className="gap-2">
                <Users className="w-4 h-4" />
                Alunos ({classStudents[group.id]?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="horarios" className="gap-2">
                <CalendarClock className="w-4 h-4" />
                Horários das Aulas
              </TabsTrigger>
            </TabsList>
            
            {/* Aba Alunos */}
            <TabsContent value="alunos">
              {loadingStudents ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                </div>
              ) : classStudents[group.id]?.length > 0 ? (
                <div className="space-y-3">
                  {/* Header com botão de gerar */}
                  {isAdmin() && (
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">
                        {classStudents[group.id].filter(s => s.call_number).length} de {classStudents[group.id].length} alunos com Nº de chamada
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="h-7 text-xs bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                        onClick={(e) => handleGenerateCallNumbers(group.id, e)}
                        disabled={generatingCallNumbers[group.id]}
                      >
                        {generatingCallNumbers[group.id] ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <RefreshCw className="w-3 h-3 mr-1" />
                        )}
                        Renumerar A-Z
                      </Button>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {classStudents[group.id].map((student) => (
                      <div key={student.id} className="flex items-center gap-2 p-2 bg-white rounded-lg border text-sm">
                        {/* Número de chamada com edição inline */}
                        {editingCallNumber === student.id ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              value={newCallNumber}
                              onChange={(e) => setNewCallNumber(e.target.value)}
                              className="w-12 h-7 text-xs text-center p-1"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveCallNumber(student.id, group.id);
                                if (e.key === 'Escape') cancelEditCallNumber();
                              }}
                            />
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-green-600" onClick={() => handleSaveCallNumber(student.id, group.id)}>
                              <Check className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-red-600" onClick={cancelEditCallNumber}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <div 
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                              student.call_number 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-gray-100 text-gray-400'
                            } ${isAdmin() ? 'cursor-pointer hover:ring-2 hover:ring-purple-300' : ''}`}
                            onClick={(e) => {
                              if (isAdmin()) {
                                e.stopPropagation();
                                setEditingCallNumber(student.id);
                                setNewCallNumber(student.call_number?.toString() || '');
                              }
                            }}
                            title={isAdmin() ? 'Clique para editar' : ''}
                          >
                            {student.call_number || '-'}
                          </div>
                        )}
                        <span className="truncate flex-1">{student.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-2">Nenhum aluno matriculado nesta turma.</p>
              )}
            </TabsContent>
            
            {/* Aba Horários */}
            <TabsContent value="horarios">
              {loadingTimeSlots ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Indicador de status */}
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <Badge variant={timeSlotData.source === 'custom' ? 'default' : 'secondary'}>
                      {timeSlotData.source === 'custom' ? 'Horário Personalizado' : 'Horário Padrão'}
                    </Badge>
                    <div className="flex gap-2">
                      {timeSlotData.source === 'custom' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleResetTimeSlots(group.id)}
                        >
                          Usar Padrão
                        </Button>
                      )}
                      <Button 
                        size="sm"
                        onClick={() => handleSaveTimeSlots(group.id)}
                        disabled={savingTimeSlots}
                      >
                        {savingTimeSlots ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Salvar
                      </Button>
                    </div>
                  </div>
                  
                  {/* Lista de horários */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Turno Manhã */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-yellow-700 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Manhã
                        </h4>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleAddTimeSlot(group.id, 'manha')}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {timeSlotData.slots
                          .filter(s => s.turno === 'manha')
                          .map((slot, idx) => {
                            const globalIdx = timeSlotData.slots.findIndex(s => s.id === slot.id);
                            return (
                              <div key={slot.id} className="flex items-center gap-2 p-2 bg-white rounded border">
                                <span className="text-xs text-muted-foreground w-6">{idx + 1}.</span>
                                <Input
                                  type="time"
                                  value={slot.start}
                                  onChange={(e) => handleTimeSlotChange(group.id, globalIdx, 'start', e.target.value)}
                                  className="w-24 h-8 text-sm"
                                />
                                <span className="text-muted-foreground">às</span>
                                <Input
                                  type="time"
                                  value={slot.end}
                                  onChange={(e) => handleTimeSlotChange(group.id, globalIdx, 'end', e.target.value)}
                                  className="w-24 h-8 text-sm"
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-500 hover:text-red-700"
                                  onClick={() => handleRemoveTimeSlot(group.id, globalIdx)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                    
                    {/* Turno Tarde */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-blue-700 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Tarde
                        </h4>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleAddTimeSlot(group.id, 'tarde')}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="space-y-1 max-h-64 overflow-y-auto">
                        {timeSlotData.slots
                          .filter(s => s.turno === 'tarde')
                          .map((slot, idx) => {
                            const globalIdx = timeSlotData.slots.findIndex(s => s.id === slot.id);
                            return (
                              <div key={slot.id} className="flex items-center gap-2 p-2 bg-white rounded border">
                                <span className="text-xs text-muted-foreground w-6">{idx + 1}.</span>
                                <Input
                                  type="time"
                                  value={slot.start}
                                  onChange={(e) => handleTimeSlotChange(group.id, globalIdx, 'start', e.target.value)}
                                  className="w-24 h-8 text-sm"
                                />
                                <span className="text-muted-foreground">às</span>
                                <Input
                                  type="time"
                                  value={slot.end}
                                  onChange={(e) => handleTimeSlotChange(group.id, globalIdx, 'end', e.target.value)}
                                  className="w-24 h-8 text-sm"
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-red-500 hover:text-red-700"
                                  onClick={() => handleRemoveTimeSlot(group.id, globalIdx)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground">
                    Estes horários serão usados ao editar o quadro de horários desta turma.
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="class-groups-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">Turmas</h1>
          <p className="text-muted-foreground mt-1">Gerenciar turmas e classes</p>
        </div>
        {canEdit() && (
          <div className="flex gap-2">
            {classGroups.length === 0 && (
              <Button variant="outline" onClick={handleSeedDefault} disabled={seeding}>
                {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GraduationCap className="w-4 h-4 mr-2" />}
                Criar Turmas Padrão
              </Button>
            )}
            <NextYearGeneratorButton onGenerated={loadClassGroups} />
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="new-class-group-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Turma
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editing ? 'Editar Turma' : 'Nova Turma'}</DialogTitle>
                  <DialogDescription>Preencha os dados da turma</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Código *</Label>
                      <Input 
                        value={formData.code} 
                        onChange={(e) => setFormData({...formData, code: e.target.value})}
                        placeholder="410"
                        required
                        data-testid="class-code"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Nome da Turma *</Label>
                      <Input 
                        value={formData.name} 
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="1º ANO A - E.F1 - 2026"
                        required
                        data-testid="class-name"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label>Sigla</Label>
                      <Input 
                        value={formData.acronym} 
                        onChange={(e) => setFormData({...formData, acronym: e.target.value})}
                        placeholder="A"
                      />
                    </div>
                    <div>
                      <Label>Ano Letivo</Label>
                      <Input 
                        type="number" 
                        value={formData.year} 
                        onChange={(e) => setFormData({...formData, year: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Turno</Label>
                      <Select value={formData.shift || undefined} onValueChange={(v) => setFormData({...formData, shift: v})}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          {shifts.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Situação</Label>
                      <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Curso/Série *</Label>
                      <Select 
                        value={formData.course_id || undefined} 
                        onValueChange={handleCourseChange}
                      >
                        <SelectTrigger><SelectValue placeholder="Selecione o curso..." /></SelectTrigger>
                        <SelectContent>
                          {courses.filter(c => c.active !== false).map(c => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name} ({c.course_type})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formData.course_series && !formData.course_id && (
                        <p className="text-xs text-muted-foreground mt-1">Atual: {formData.course_series}</p>
                      )}
                    </div>
                    <div>
                      <Label>Nível de Ensino</Label>
                      <Select value={formData.education_level || undefined} onValueChange={(v) => setFormData({...formData, education_level: v})}>
                        <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                        <SelectContent>
                          {levels.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label>Mín. Alunos</Label>
                      <Input 
                        type="number" 
                        value={formData.min_students} 
                        onChange={(e) => setFormData({...formData, min_students: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Máx. Alunos</Label>
                      <Input 
                        type="number" 
                        value={formData.max_students} 
                        onChange={(e) => setFormData({...formData, max_students: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Sala</Label>
                      <Input 
                        value={formData.room} 
                        onChange={(e) => setFormData({...formData, room: e.target.value})}
                        placeholder="Sala 01"
                      />
                    </div>
                    <div>
                      <Label>Professor</Label>
                      <Input 
                        value={formData.teacher} 
                        onChange={(e) => setFormData({...formData, teacher: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Link do Google Classroom</Label>
                    <Input 
                      value={formData.google_classroom_link} 
                      onChange={(e) => setFormData({...formData, google_classroom_link: e.target.value})}
                      placeholder="https://classroom.google.com/c/..."
                    />
                    {editing?.google_classroom_course_id ? (
                      <p
                        className="text-xs text-green-600 mt-1 flex items-center gap-1"
                        data-testid="classroom-linked-indicator"
                      >
                        <span>✓</span>
                        <span>
                          Vinculado a{' '}
                          <strong>
                            {editing.google_classroom_course_name || editing.google_classroom_course_id}
                          </strong>
                        </span>
                      </p>
                    ) : null}
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" data-testid="save-class-group">
                      {editing ? 'Salvar' : 'Criar'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>

      {/* Filtros */}
      <Card className="border-border/60">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por código, nome ou curso..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Ano" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {availableYears.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterShift} onValueChange={setFilterShift}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Turno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {shifts.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterLevel} onValueChange={setFilterLevel}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Nível" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {levels.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-border/60">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <GraduationCap className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total de Turmas</p>
              <p className="text-2xl font-bold">{filteredGroups.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total de Alunos</p>
              <p className="text-2xl font-bold">{filteredGroups.reduce((sum, g) => sum + (g.student_count || 0), 0)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-yellow-500/10 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Turmas Manhã</p>
              <p className="text-2xl font-bold">{filteredGroups.filter(g => g.shift === 'Manhã').length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Turmas Tarde</p>
              <p className="text-2xl font-bold">{filteredGroups.filter(g => g.shift === 'Tarde').length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/60">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Building className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Ciclos</p>
              <p className="text-2xl font-bold">{Object.keys(groupedByLevel).length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Turmas Agrupadas */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : Object.keys(groupedByLevel).length === 0 ? (
        <Card className="border-border/60">
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Nenhuma turma encontrada</p>
            {canEdit() && (
              <Button className="mt-4" onClick={handleSeedDefault} disabled={seeding}>
                Criar Turmas Padrão
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        sortedLevels.map((level) => {
          const groups = groupedByLevel[level];
          const totalAlunos = groups.reduce((sum, g) => sum + (g.student_count || 0), 0);
          const turnoManha = groups.filter(g => g.shift === 'Manhã');
          const turnoTarde = groups.filter(g => g.shift === 'Tarde');
          
          return (
            <Card key={level} className={`border-border/60 border-l-4 ${getLevelColor(level)}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{getLevelIcon(level)}</span>
                    {level}
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge variant="secondary" className="text-sm">
                      {groups.length} turmas
                    </Badge>
                    <Badge variant="outline" className="text-sm bg-green-50 text-green-700 border-green-200">
                      <Users className="w-3 h-3 mr-1" />
                      {totalAlunos} alunos
                    </Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Sub-agrupamento por turno */}
                <div className="space-y-4">
                  {turnoManha.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2 px-2">
                        <Badge className="bg-yellow-100 text-yellow-700">
                          <Clock className="w-3 h-3 mr-1" />
                          Manhã
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {turnoManha.length} turmas • {turnoManha.reduce((sum, g) => sum + (g.student_count || 0), 0)} alunos
                        </span>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-20">Código</TableHead>
                            <TableHead>Nome da Turma</TableHead>
                            <TableHead className="w-28 text-center">Alunos</TableHead>
                            <TableHead className="w-24">Situação</TableHead>
                            <TableHead className="w-32 text-center">Nº Chamada</TableHead>
                            <TableHead className="w-20">Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {turnoManha.sort((a, b) => a.name.localeCompare(b.name)).map((group) => (
                            <React.Fragment key={group.id}>
                              <TableRow data-testid={`class-group-${group.id}`} className="cursor-pointer hover:bg-muted/50" onClick={() => loadStudentsForClass(group)}>
                                <TableCell className="font-mono text-sm">{group.code}</TableCell>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    {expandedClass === group.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                    {group.name}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="outline" className="font-mono">
                                    <Users className="w-3 h-3 mr-1" />
                                    {group.student_count || 0}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge className={getStatusBadge(group.status)}>{group.status || 'Aberta'}</Badge>
                                </TableCell>
                                <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                  {isAdmin() && (
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      className="h-7 text-xs bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                                      onClick={(e) => handleGenerateCallNumbers(group.id, e)}
                                      disabled={generatingCallNumbers[group.id] || !group.student_count}
                                      data-testid={`generate-call-numbers-${group.id}`}
                                    >
                                      {generatingCallNumbers[group.id] ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <>
                                          <Hash className="w-3 h-3 mr-1" />
                                          Gerar A-Z
                                        </>
                                      )}
                                    </Button>
                                  )}
                                </TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  {canEdit() && (
                                    <Button variant="ghost" size="icon" onClick={() => handleEdit(group)}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                              {renderExpandedRow(group)}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  
                  {turnoTarde.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2 px-2">
                        <Badge className="bg-blue-100 text-blue-700">
                          <Clock className="w-3 h-3 mr-1" />
                          Tarde
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {turnoTarde.length} turmas • {turnoTarde.reduce((sum, g) => sum + (g.student_count || 0), 0)} alunos
                        </span>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-20">Código</TableHead>
                            <TableHead>Nome da Turma</TableHead>
                            <TableHead className="w-28 text-center">Alunos</TableHead>
                            <TableHead className="w-24">Situação</TableHead>
                            <TableHead className="w-32 text-center">Nº Chamada</TableHead>
                            <TableHead className="w-20">Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {turnoTarde.sort((a, b) => a.name.localeCompare(b.name)).map((group) => (
                            <React.Fragment key={group.id}>
                              <TableRow data-testid={`class-group-${group.id}`} className="cursor-pointer hover:bg-muted/50" onClick={() => loadStudentsForClass(group)}>
                                <TableCell className="font-mono text-sm">{group.code}</TableCell>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    {expandedClass === group.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                    {group.name}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="outline" className="font-mono">
                                    <Users className="w-3 h-3 mr-1" />
                                    {group.student_count || 0}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge className={getStatusBadge(group.status)}>{group.status || 'Aberta'}</Badge>
                                </TableCell>
                                <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                                  {isAdmin() && (
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      className="h-7 text-xs bg-green-50 text-green-700 border-green-300 hover:bg-green-100"
                                      onClick={(e) => handleGenerateCallNumbers(group.id, e)}
                                      disabled={generatingCallNumbers[group.id] || !group.student_count}
                                      data-testid={`generate-call-numbers-${group.id}`}
                                    >
                                      {generatingCallNumbers[group.id] ? (
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                      ) : (
                                        <>
                                          <Hash className="w-3 h-3 mr-1" />
                                          Gerar A-Z
                                        </>
                                      )}
                                    </Button>
                                  )}
                                </TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  {canEdit() && (
                                    <Button variant="ghost" size="icon" onClick={() => handleEdit(group)}>
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                              {renderExpandedRow(group)}
                            </React.Fragment>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}

// V6.3 · Gerador de Turmas do Próximo Ano
function NextYearGeneratorButton({ onGenerated }) {
  const [open, setOpen] = React.useState(false);
  const [srcYear, setSrcYear] = React.useState(String(new Date().getFullYear()));
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [overrides, setOverrides] = React.useState({}); // source_id → { capacity, skip }
  const [extraClasses, setExtraClasses] = React.useState([]); // [{name, segment, capacity, period}]

  const addExtra = () => setExtraClasses((arr) => [...arr, { name: '', segment: '', capacity: 30, period: '' }]);
  const updExtra = (i, k, v) => setExtraClasses((arr) => arr.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  const rmExtra = (i) => setExtraClasses((arr) => arr.filter((_, idx) => idx !== i));

  const doPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/class-groups/next-year/preview', { source_year: Number(srcYear) });
      setPreview(data);
    } catch (err) { toast.error('Erro: ' + err.message); }
    finally { setLoading(false); }
  };
  const doGenerate = async () => {
    setLoading(true);
    try {
      const payload = {
        source_year: Number(srcYear),
        overrides: Object.entries(overrides).map(([source_id, o]) => ({ source_id, ...o })),
        extra_classes: extraClasses.filter((e) => e.name && e.name.trim()).map((e) => ({
          name: e.name.trim(), segment: e.segment || undefined,
          capacity: Number(e.capacity) || 30, period: e.period || undefined,
        })),
      };
      const { data } = await api.post('/class-groups/next-year/generate', payload);
      setResult(data);
      toast.success(`${data.created} turmas criadas para ${data.target_year}.`);
      onGenerated && onGenerated();
    } catch (err) { toast.error('Erro: ' + (err?.response?.data?.detail || err.message)); }
    finally { setLoading(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="btn-next-year" className="border-purple-300 text-purple-700">
        ✨ Gerar Próximo Ano
      </Button>
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setPreview(null); setResult(null); setOverrides({}); } setOpen(v); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preparar ano letivo {preview?.target_year || Number(srcYear) + 1}</DialogTitle>
            <DialogDescription>
              Vamos usar as turmas de {srcYear} como base para preparar a estrutura acadêmica de {Number(srcYear) + 1}. Nenhum aluno será movimentado.
            </DialogDescription>
          </DialogHeader>

          {result ? (
            <div className="space-y-3">
              <div className="text-center py-4">
                <div className="text-4xl">✨</div>
                <div className="text-lg font-medium mt-2">Ano letivo {result.target_year} preparado</div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="border rounded p-3 bg-green-50">
                  <div className="text-2xl font-bold text-green-700">{result.created}</div>
                  <div className="text-xs">Turmas criadas</div>
                </div>
                <div className="border rounded p-3 bg-purple-50">
                  <div className="text-2xl font-bold">{result.already_existed}</div>
                  <div className="text-xs">Já existentes</div>
                </div>
                <div className="border rounded p-3 bg-gray-50">
                  <div className="text-2xl font-bold">{(result.skipped || []).length}</div>
                  <div className="text-xs">Puladas</div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">Batch id: <code>{result.batch_id}</code></div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setOpen(false)}>Fechar</Button>
              </div>
            </div>
          ) : preview ? (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2 text-center text-sm">
                <StatBox label="Analisadas" value={preview.analyzed} />
                <StatBox label="Serão criadas" value={preview.will_create} />
                <StatBox label="Já existem" value={preview.already_exists} />
                <StatBox label="Cap. projetada" value={preview.total_capacity_projected} />
              </div>
              <div className="border rounded max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0"><tr>
                    <th className="text-left p-2">Turma {preview.source_year}</th>
                    <th className="text-left p-2">Proposta {preview.target_year}</th>
                    <th className="p-2">Capacidade</th>
                    <th className="p-2">Status</th>
                  </tr></thead>
                  <tbody>
                    {preview.classes.map((c) => (
                      <tr key={c.source_id} className="border-t">
                        <td className="p-2">{c.source_name}</td>
                        <td className="p-2">{c.proposed_name}</td>
                        <td className="p-2 text-center">
                          <Input type="number" defaultValue={c.capacity}
                            className="w-20 h-8 text-center"
                            onChange={(e) => setOverrides({ ...overrides, [c.source_id]: { ...(overrides[c.source_id] || {}), capacity: Number(e.target.value) } })} />
                        </td>
                        <td className="p-2 text-center">
                          {c.status === 'exists' ? (
                            <Badge variant="outline" className="bg-blue-100">✓ Já existe</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-100">Criar</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setPreview(null)}>Voltar</Button>
                <Button onClick={doGenerate} disabled={loading} className="bg-purple-600 hover:bg-purple-700" data-testid="btn-confirm-generate">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Confirmar e gerar {preview.target_year}
                </Button>
              </div>
              {/* V6.5 · Editor de turmas extras */}
              <div className="border-t pt-3 space-y-2">
                <div className="flex justify-between items-center">
                  <div className="text-xs font-medium">Turmas extras (opcional)</div>
                  <Button size="sm" variant="outline" onClick={addExtra} data-testid="btn-add-extra-class">
                    + Adicionar turma extra
                  </Button>
                </div>
                {extraClasses.length > 0 && (
                  <div className="space-y-1">
                    {extraClasses.map((e, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center text-xs">
                        <Input placeholder="Nome (ex.: 3º ANO C - 2027)" className="col-span-4 h-8" value={e.name}
                          onChange={(ev) => updExtra(i, 'name', ev.target.value)} data-testid={`extra-name-${i}`} />
                        <Input placeholder="Segmento" className="col-span-3 h-8" value={e.segment}
                          onChange={(ev) => updExtra(i, 'segment', ev.target.value)} />
                        <Input placeholder="Turno" className="col-span-2 h-8" value={e.period}
                          onChange={(ev) => updExtra(i, 'period', ev.target.value)} />
                        <Input type="number" placeholder="Cap." className="col-span-2 h-8" value={e.capacity}
                          onChange={(ev) => updExtra(i, 'capacity', ev.target.value)} />
                        <Button size="sm" variant="ghost" className="col-span-1 h-8 text-red-600" onClick={() => rmExtra(i)}>✕</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs">Ano de origem</label>
                  <Input value={srcYear} onChange={(e) => setSrcYear(e.target.value)} type="number" data-testid="next-year-src" />
                </div>
                <div>
                  <label className="text-xs">Ano de destino</label>
                  <Input value={Number(srcYear) + 1} disabled />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={doPreview} disabled={loading} data-testid="btn-preview-next-year">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Visualizar prévia
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="border rounded p-2 bg-purple-50/40">
      <div className="text-xl font-bold">{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

